import { Pagination, Select, Table } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import Icon from "../../../components/Icon/Icon";
import Spinner from '../../../components/Spinner/Spinner';
import Tag from "../../../components/Tag/Tag";

import { pageSize, useRatingsTab } from "./useRatingsTab";

import { Props, Rating, Score, Status, TableData, TabStatus, User } from "./RatingsTab.model";

import styles from "./RatingsTab.module.scss";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";

const RatingsTab = ({ filters }: Props) => {
	const {
		assignRating,
		formatDate,
		getRatings,
		isAssignedAdminDeleted,
		onChangeRatingsPagination,
		reassignRating,
		state
	} = useRatingsTab({ filters });
	const { admins, ratings, ratingsPagination, tabStatus } = state;
	const intl = useIntl();
	const history = useHistory();

	const getBadgeByScore = (score: Score) => {
		switch (score) {
			case 'One':
			case 'Two': return <div className={styles.score}>
				<Icon name="bad-star" type="custom" color="gray" width="20px"  height="20px" />
				<span><FormattedMessage id="ratings.score.substandard" /></span>
			</div>
			case 'Three':
			case 'Four': return <div className={styles.score}>
				<Icon name="star" color="accent" type="solid" width="16px"  height="16px" />
				<span><FormattedMessage id="ratings.score.good" /></span>
			</div>
			case 'Five': return <div className={styles.score}>
				<Icon name="stars" color="blue"  type="solid"  width="16px"  height="16px" />
				<span><FormattedMessage id="ratings.score.exceptional" /></span>
			</div>
		}
	}

	const getTagColorByStatus = (status: Status) => {
		switch (status) {
			case 'Assigned': return "grey";
			case 'Accepted': return "green";
			case 'Unassigned': return "text";
			case 'Rejected': return "red";
		}
	}

	const columnsRatings = [
		{
			dataIndex: 'userRated',
			render: (userRated: User) => (<div className={styles.flexCell}>
				<div className={styles.avatar}>
					{userRated.imageId && !userRated.isDeleted ? <img src={userRated.imageId} alt="" /> :
						<Icon
							color="light"
							height="12px"
							name="user"
							width="12px"
						/>}
				</div>
				{!userRated.isDeleted ? `${userRated.firstName}  ${userRated.lastName}` :  intl.formatMessage({id: "userLabels.deletedUser"})}
			</div>),
			title: intl.formatMessage({ id: 'table.column.mentor' }),
		},
		{
			dataIndex: 'score',
			render: (score: Score) => (
				getBadgeByScore(score)
			),
			title: intl.formatMessage({ id: 'table.column.score' }),
		},
		{
			dataIndex: 'ratedAt',
			render: (ratedAt: string) => (
				formatDate(ratedAt)
			),
			title: intl.formatMessage({ id: 'table.column.date' }),
		},
		{
			render: (record: Rating) => (
				<Select className={styles.select}
					defaultValue={
						record.assignedTo
							? !isAssignedAdminDeleted(record.assignedTo)
								? record.assignedTo
								: intl.formatMessage({ id: 'userLabels.deletedUser' })
							: undefined
					}
					onClick={(event) => { event.stopPropagation() }} onChange={(value) => {
					if (record.assignedTo) reassignRating(value, record)
					else assignRating(value, record);
				}} placeholder={<div className={styles.placeholder}><FormattedMessage id="ratings.assignNow" /></div>}>
					{admins?.map((admin, index) => {
						return <Select.Option value={admin.userId} key={admin.userId}>
							<div className={styles.option}>
								<div className={styles.avatar}>
									{admin.imageId && !admin.isDeleted ?
										<img src={admin.imageId} alt="" /> :
										<Icon
											color="light"
											height="12px"
											name="user"
											width="12px"
										/>}
								</div>
								<span>{!admin.isDeleted ? `${admin.firstName} ${admin.lastName}`  : intl.formatMessage({id: "userLabels.deletedUser"})}</span>
							</div>
						</Select.Option>
					})}
				</Select>
			),
			title: intl.formatMessage({ id: 'table.column.assigned' }),
		},
		{
			dataIndex: 'status',
			render: (status: Status) => (
				<Tag noBackground color={getTagColorByStatus(status)}><div className={styles.flexTag}>{status === "Unassigned" && <div className={styles.redDot}/>}{intl.formatMessage({ id: `status.rating.${status}` })}</div></Tag>
			),
			title: intl.formatMessage({ id: 'table.column.status' }),
		},
	];

	const onClickRow = (rowData: TableData) => {
    return {
			onClick: () => {
				history.push(`/rating/${rowData.user.userId}/${rowData.userRated.userId}`);
			},
    };
  }

	return (
		<div className={styles.tabContent}>
			<Spinner spinning={tabStatus === TabStatus.Loading} />
			<ErrorMessage error={tabStatus === TabStatus.Error} onRefresh={getRatings} showReport={false}/>
			<div className={styles.table}>
				<Table
					columns={columnsRatings}
					dataSource={ratings}
					pagination={false}
					onRow={onClickRow}
					rowKey={(record) => {
						return `${record.user.userId}${record.userRated.userId}`;
					}}
				/>
			</div>
			<div className={styles.contentMobile}>
				{ratings &&
					ratings.map((rating, key) => (
						<div
							className={styles.container}
							key={key}
							onClick={() =>
								history.push(
									`/rating/${rating.user.userId}/${rating.userRated.userId}`
								)
							}
						>
							<div className={`${styles.wrapper} ${styles.borderBottom}`}>
								<div className={styles.userInfo}>
									<div className={styles.iconContainer}>
										{rating.userRated?.imageId ? (
											<img
												alt=""
												src={rating.userRated?.imageId}
												className={styles.avatar}
											/>
										) : (
											<div className={styles.avatar}>
												<Icon
													color="text"
													height="12px"
													name="user"
													width="12px"
												/>
											</div>
										)}
									</div>
									<div className={styles.userContainer}>
										{rating.userRated.firstName} {rating.userRated.lastName}
									</div>
								</div>
							</div>
							<div className={styles.info}>
								<div>
									<h2>
										<FormattedMessage id="table.column.score" />
									</h2>
								</div>
								<div>{getBadgeByScore(rating.score)}</div>
							</div>
							<div className={styles.info}>
								<div>
									<h2>
										<FormattedMessage id="table.column.date" />
									</h2>
								</div>
								<span>{formatDate(rating.ratedAt)}</span>
							</div>
							<div className={styles.info}>
								<div>
									<h2>
										<FormattedMessage id="table.column.status" />
									</h2>
								</div>
								<Tag noBackground color={getTagColorByStatus(rating.status)}>
									<div className={styles.flexTag}>
										{rating.status === 'Unassigned' && (
											<div className={styles.redDot} />
										)}
										{intl.formatMessage({
											id: `status.rating.${rating.status}`,
										})}
									</div>
								</Tag>
							</div>
							<div className={styles.assign}>
								<Select className={styles.select} 	defaultValue={
									rating.assignedTo
										? !isAssignedAdminDeleted(rating.assignedTo)
											? rating.assignedTo
											: intl.formatMessage({ id: 'userLabels.deletedUser' })
										: undefined
								} onClick={(event) => { event.stopPropagation() }} onChange={(value) => {
									if (rating.assignedTo) reassignRating(value, rating)
									else assignRating(value, rating);
								}} placeholder={<div className={styles.placeholder}><FormattedMessage id="ratings.assignNow" /></div>}>
									{admins?.map((admin, index) => {
										return <Select.Option value={admin.userId} key={admin.userId}>
											<div className={styles.option}>
												<div className={styles.avatar}>
													{admin.imageId ?
														<img src={admin.imageId} alt="" /> :
														<Icon
															color="light"
															height="12px"
															name="user"
															width="12px"
														/>}
												</div>
												<span>{admin.firstName} {admin.lastName}</span>
											</div>
										</Select.Option>
									})}
								</Select>
							</div>
						</div>
					))}
			</div>
			<div className={styles.paginationWrapper}>
				<Pagination
					current={ratingsPagination.page}
					pageSize={pageSize}
					total={ratingsPagination.total}
					onChange={onChangeRatingsPagination}
					showSizeChanger={false}
				/>
			</div>
		</div>
	);
}
export default RatingsTab;